import React, { useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import {
  Box,
  Divider,
  FormControl,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PasswordIcon from "@mui/icons-material/Password";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import WarningIcon from "@mui/icons-material/Warning";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import EmailIcon from "@mui/icons-material/Email";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  idSelected,
  getUser,
  snackDisplayed,
  messagesDisplayed,
  enableUser,
  disableUser,
  forcePasswordReset,
  emailUserPassword,
  enableMfa,
  disableMfa,
  getUserRoles,
  sendWelcomeEmail,
  getUserOrganizations,
  getUserApplications
} from "../../app/userSlice";
import VerticalTabDetailPanel from "@lp/lp-ui/src/components/panels/VerticalTabPanel";
import UserBasicInfoPanel from "./UserBasicInfoPanel";
import UserAddressPanel from "./UserAddressPanel";
import UserContactsPanel from "./UserContactsPanel";
import {
  BreadcrumbSeparator,
  LoadingBackDrop,
  TitleBar,
  SnackDisplay,
  MessageDialog,
  PrimarySaveConfirmationDialog,
} from "@lp/lp-ui";
import UserApplicationsPanel from "./UserApplicationsPanel";
import UserRolesPanel from "./UserRolesPanel";
import { useCurrentUser } from "../../app/useCurrentUser";
import { GetLocalDateTimeString } from "../../DateUtilities";
import { TitleBarLabel } from "@lp/lp-ui/src/components/TitleBar";
import UserResetPasswordDialog from "./UserResetPasswordDialog";
import {
  ApiViewEntityResponse,
  UserPasswordModel,
  UserProfileResponseModel,
} from "../../typings";
import { AuditLogDialog } from "./AuditLogDialog";
import UserPasswordDialog from "../UserPasswordDialog";
import UserQlikPanel from "./UserQlikPanel";
import UserOrganizationsPanel from "./UserOrganizationsPanel";
import UserOrganizationRolesPanel from "./UserOrganizationRolesPanel";
import { UserDisplayName } from "../../functions";

const defaultTabs = [
  {
    tabComponent: <Tab label="General Information" key="vtp-gi" />,
    panelComponent: <UserBasicInfoPanel />
  },
  {
    tabComponent: <Tab label="Address" key="vtp-addr" />,
    panelComponent: <UserAddressPanel />
  },
  {
    tabComponent: <Tab label="Contacts" key="vtp-c" />,
    panelComponent: <UserContactsPanel />
  },
  {
    tabComponent: <Tab label="Applications" key="vtp-apps" />,
    panelComponent: <UserApplicationsPanel />
  },
  {
    tabComponent: <Tab label="User Roles" key="vtp-usr-roles" />,
    panelComponent: <UserRolesPanel />
  }
];

export default function User() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [isAdmin, isTenantAdmin, _] = useCurrentUser();
  const { data, welcomeEmailSent, messages, loading, snack } = useAppSelector(
    (store) => store.user
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [disableUserDialogOpen, setDisableUserDialogOpen] = useState(false);
  const [disableMfaDialogOpen, setDisableMfaDialogOpen] = useState(false);
  const [resetUserPasswordDialogOpen, setResetUserPasswordDialogOpen] =
    useState(false);
  const [sendWelcomeEmailDialogOpen, setSendWelcomeEmailDialogOpen] =
    useState(false);
  const [userResetPasswordData, setUserResetPasswordData] =
    useState<UserPasswordModel | null>(null);
  const [userPasswordData, setUserPasswordData] =
    useState<UserPasswordModel | null>(null);
  const [auditLogDialogOpen, setAuditLogDialogOpen] = useState(false);

  const [currentTabs, setCurrentTabs] = useState(defaultTabs);
  useEffect(() => {
    const tabs = [...defaultTabs];
    if (isTenantAdmin) {
      const orgsIndex = tabs.findIndex(
        (tab) => tab.tabComponent.key === "vtp-orgs"
      );
      if (orgsIndex === -1) {
        tabs.push({
          tabComponent: <Tab label="Organizations" key="vtp-orgs" />,
          panelComponent: <UserOrganizationsPanel />
        });
      }
      const orgRolesIndex = tabs.findIndex(
        (tab) => tab.tabComponent.key === "vtp-orgs-roles"
      );
      if (orgRolesIndex !== -1) {
        tabs.splice(orgRolesIndex, 1);
      }
    } else {
      const orgRolesIndex = tabs.findIndex(
        (tab) => tab.tabComponent.key === "vtp-orgs-roles"
      );
      if (orgRolesIndex === -1) {
        tabs.push({
          tabComponent: <Tab label="Organization" key="vtp-orgs-roles" />,
          panelComponent: <UserOrganizationRolesPanel />
        });
      }
      const orgsIndex = tabs.findIndex(
        (tab) => tab.tabComponent.key === "vtp-orgs"
      );
      if (orgsIndex !== -1) {
        tabs.splice(orgsIndex, 1);
      }
    }
    if (isAdmin) {
      tabs.push({
        tabComponent: <Tab label="Qlik" key="vtp-qlik" />,
        panelComponent: <UserQlikPanel />
      });
    } else {
      const index = tabs.findIndex(
        (tab) => tab.tabComponent.key === "vtp-qlik"
      );
      if (index !== -1) {
        tabs.splice(index, 1);
      }
    }
    setCurrentTabs(tabs);
  }, [isAdmin, isTenantAdmin]);

  useEffect(() => {
    const userId = Number(id);
    dispatch(idSelected(userId));
    dispatch(getUser(userId));
    dispatch(getUserRoles(userId));
    dispatch(getUserApplications(userId));
    dispatch(getUserOrganizations(userId));
  }, [id]);

  const handleMenuOpenClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenAuditLogDialog = () => {
    setAuditLogDialogOpen(true);
    handleMenuClose();
  };

  const handleUserEnabledChange = (enabled: boolean) => {
    if (enabled) {
      dispatch(enableUser(Number(id)));
    } else {
      setDisableUserDialogOpen(true);
    }
  };

  const handleMfaEnabledChange = (enabled: boolean) => {
    if (enabled) {
      dispatch(enableMfa(Number(id)));
    } else {
      setDisableMfaDialogOpen(true);
    }
  };

  const handleForcePasswordReset = () => {
    setResetUserPasswordDialogOpen(true);
  };

  const handleSendWelcomeEmail = () => {
    setSendWelcomeEmailDialogOpen(true);
  };

  const handleDisableUserDialogClose = () => {
    setDisableUserDialogOpen(false);
  };

  const handleDisableMfaDialogClose = () => {
    setDisableMfaDialogOpen(false);
  };

  const handleDisableUserDialogSave = () => {
    handleDisableUserDialogClose();
    dispatch(disableUser(Number(id)));
  };

  const handleDisableMfaDialogSave = () => {
    handleDisableMfaDialogClose();
    dispatch(disableMfa(Number(id)));
  };

  const handleResetUserPasswordDialogClose = () => {
    setResetUserPasswordDialogOpen(false);
  };

  const handleSendWelcomeEmailDialogClose = () => {
    setSendWelcomeEmailDialogOpen(false);
  };

  const handleResetUserPasswordDialogSave = async () => {
    handleResetUserPasswordDialogClose();
    const result = await dispatch(forcePasswordReset(Number(id)));
    if (result.meta.requestStatus === "fulfilled") {
      const userData = result.payload as ApiViewEntityResponse<UserProfileResponseModel>;
      setUserResetPasswordData(userData.data);
    }
  };

  const handleSendWelcomeEmailDialogSave = async () => {
    handleSendWelcomeEmailDialogClose();
    const result = await dispatch(sendWelcomeEmail(Number(id)));
    if (result.meta.requestStatus === "fulfilled") {
      const userData = result.payload as ApiViewEntityResponse<UserProfileResponseModel>;
      setUserPasswordData(userData.data);
    }
  };

  const handleResetPasswordDialogClose = () => {
    setUserResetPasswordData(null);
  };

  const handlePasswordDialogClose = () => {
    setUserPasswordData(null);
  };

  const handleResetPasswordDialogEmailPassword = async () => {
    if (userResetPasswordData) {
      dispatch(emailUserPassword(userResetPasswordData));
    }
    handleResetPasswordDialogClose();
  };

  return (
    <React.Fragment>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TitleBar
            titleComponent={
              <Box py={1.5}>
                <Typography variant="h3" color="primary">
                  <Box display="flex">
                    <NavLink to="/users">Users</NavLink>
                    <BreadcrumbSeparator />
                    {data && UserDisplayName(data)}
                  </Box>
                </Typography>
              </Box>
            }
            centerComponent={
              <Box
                className="hidden-xs visible-md"
                display="flex"
                justifyContent="flex-end"
                width="100%"
                pr={3}
                sx={{ visibility: { xs: "hidden", md: "visible" } }}
              >
                <FormControl sx={{ marginRight: 3 }}>
                  <TitleBarLabel
                    label="Enabled"
                    value={data?.enabled ? "Yes" : "No"}
                  />
                </FormControl>
                <FormControl sx={{ marginRight: 3 }}>
                  <TitleBarLabel
                    label="MFA Enabled"
                    value={data?.twoFactorEnabled ? "Yes" : "No"}
                  />
                </FormControl>
                <FormControl sx={{ marginRight: 3 }}>
                  <TitleBarLabel
                    label="Welcome Email Sent"
                    value={
                      data?.welcomeEmailSent || welcomeEmailSent ? "Yes" : "No"
                    }
                  />
                </FormControl>
                <FormControl sx={{ marginRight: 3 }}>
                  <TitleBarLabel
                    label="Date Created"
                    value={data?.dateCreatedUtc ? GetLocalDateTimeString(data.dateCreatedUtc) : "-"}
                  />
                </FormControl>
                <FormControl sx={{ marginRight: 3 }}>
                  <TitleBarLabel
                    label="Last Login Date"
                    value={data?.lastLoginDateUtc ? GetLocalDateTimeString(data.lastLoginDateUtc) : "-"}
                  />
                </FormControl>
              </Box>
            }
            actionComponents={[
              <IconButton
                key="title-menu"
                sx={{ marginLeft: 2, marginRight: -1 }}
                onClick={handleMenuOpenClick}
              >
                <MoreVertIcon />
              </IconButton>
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <VerticalTabDetailPanel tabs={currentTabs} />
        </Grid>
      </Grid>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          disabled={!isAdmin}
          onClick={() => {
            handleMenuClose();
            handleUserEnabledChange(!data?.enabled);
          }}
        >
          <ListItemIcon>
            {data?.enabled ? <PersonOffIcon /> : <PersonIcon />}
          </ListItemIcon>
          <ListItemText>{data?.enabled ? "Disable" : "Enable"}</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!isAdmin || !data?.enabled}
          onClick={() => {
            handleMenuClose();
            handleForcePasswordReset();
          }}
        >
          <ListItemIcon>
            <PasswordIcon />
          </ListItemIcon>
          <ListItemText>Reset Password</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!isAdmin || !data?.enabled}
          onClick={() => {
            handleMenuClose();
            handleSendWelcomeEmail();
          }}
        >
          <ListItemIcon>
            <EmailIcon />
          </ListItemIcon>
          <ListItemText>Send Welcome Email</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!isAdmin || !data?.enabled}
          onClick={() => {
            handleMenuClose();
            handleMfaEnabledChange(!data?.twoFactorEnabled);
          }}
        >
          <ListItemIcon>
            {data?.twoFactorEnabled ? <WarningIcon /> : <SecurityIcon />}
          </ListItemIcon>
          <ListItemText>
            {data?.twoFactorEnabled ? "MFA Disable" : "MFA Enable"}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleOpenAuditLogDialog}>
          <ListItemIcon>
            <SpeakerNotesIcon />
          </ListItemIcon>
          <ListItemText>View Audit Logs</ListItemText>
        </MenuItem>
      </Menu>

      <MessageDialog
        title="Attention"
        messages={messages}
        handleClose={() => {
          dispatch(messagesDisplayed([]));
        }}
      />

      <SnackDisplay
        snack={snack}
        handleClose={() => {
          dispatch(snackDisplayed(null));
        }}
      />

      <PrimarySaveConfirmationDialog
        open={disableUserDialogOpen}
        title={`Disable login for ${UserDisplayName(data)}`}
        contentText="The disable action will prevent edits to this user and prevent them from accessing any applications."
        actionButtonText="Disable"
        handleClose={handleDisableUserDialogClose}
        handleSave={handleDisableUserDialogSave}
      />

      <PrimarySaveConfirmationDialog
        open={resetUserPasswordDialogOpen}
        title={`Reset password for ${UserDisplayName(data)}`}
        contentText="A password will automatically be generated. You will be able to view and copy the password in the next step."
        actionButtonText="Reset"
        handleClose={handleResetUserPasswordDialogClose}
        handleSave={handleResetUserPasswordDialogSave}
      />

      <PrimarySaveConfirmationDialog
        open={sendWelcomeEmailDialogOpen}
        title={`Send welcome email to ${UserDisplayName(data)}`}
        contentText="A password will automatically be generated. You will be able to view and copy the password in the next step."
        actionButtonText="Send"
        handleClose={handleSendWelcomeEmailDialogClose}
        handleSave={handleSendWelcomeEmailDialogSave}
      />

      <PrimarySaveConfirmationDialog
        open={disableMfaDialogOpen}
        title={`Disable MFA for ${UserDisplayName(data)}`}
        contentText="The disable MFA action will have an impact on the security of the user account."
        actionButtonText="Disable"
        handleClose={handleDisableMfaDialogClose}
        handleSave={handleDisableMfaDialogSave}
      />

      <UserResetPasswordDialog
        userPwdData={userResetPasswordData}
        handleClose={handleResetPasswordDialogClose}
        handleEmailPassword={handleResetPasswordDialogEmailPassword}
      />

      <UserPasswordDialog
        userPwdData={userPasswordData}
        allowEmailUser={false}
        handleClose={handlePasswordDialogClose}
        handleEmailNewUser={() => {}}
      />

      <AuditLogDialog
        open={auditLogDialogOpen}
        handleClose={() => {
          setAuditLogDialogOpen(false);
        }}
      />

      <LoadingBackDrop open={loading} />
    </React.Fragment>
  );
}
